import Logo from '../components/Logo'

const BLUE = '#185FA5'
const SECTIONS = [
  { id: 'getting-started', label: 'Getting Started' },
  { id: 'managing-squad', label: 'Managing Your Squad' },
  { id: 'fixtures', label: 'Fixtures' },
  { id: 'during-match', label: 'During a Match' },
  { id: 'after-match', label: 'After a Match' },
  { id: 'sharing', label: 'Sharing with Parents' },
  { id: 'roles', label: 'Roles & Permissions' },
  { id: 'faq', label: 'FAQ' },
  { id: 'feedback', label: 'Feedback & Feature Requests' },
]

function Section({ id, title, children }) {
  return (
    <div id={id} style={{ marginBottom: 48, scrollMarginTop: 80 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: BLUE, marginBottom: 16, paddingBottom: 8, borderBottom: '2px solid #e8f0fb' }}>
        {title}
      </h2>
      {children}
    </div>
  )
}

function Q({ q, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#333', marginBottom: 6 }}>{q}</div>
      <div style={{ fontSize: 14, color: '#555', lineHeight: 1.7 }}>{children}</div>
    </div>
  )
}

function Step({ num, title, children }) {
  return (
    <div style={{ display: 'flex', gap: 14, marginBottom: 20 }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', background: BLUE, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0, marginTop: 2 }}>
        {num}
      </div>
      <div>
        <div style={{ fontSize: 15, fontWeight: 600, color: '#333', marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 14, color: '#555', lineHeight: 1.7 }}>{children}</div>
      </div>
    </div>
  )
}

function Badge({ color, bg, children }) {
  return (
    <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 20, background: bg, color, fontSize: 13, fontWeight: 600, marginRight: 6, marginBottom: 4 }}>
      {children}
    </span>
  )
}

export default function Help() {
  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #0d3d6b 0%, #185FA5 100%)', padding: '20px 16px 32px' }}>
        <div style={{ maxWidth: 780, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
            <Logo width="360px" maxWidth="360px" variant="light" />
            <nav style={{ marginLeft: 'auto', display: 'flex', gap: 16 }}>
              <span onClick={() => window.location.href = '/'} style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', cursor: 'pointer' }}>Home</span>
              <span onClick={() => window.location.href = '/contact'} style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', cursor: 'pointer' }}>Contact</span>
            </nav>
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#fff', marginBottom: 8 }}>Help & Getting Started</h1>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.8)', lineHeight: 1.6 }}>
            Everything you need to get your club up and running on GAA Match Tracker.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 780, margin: '0 auto', padding: '0 16px 60px' }}>
        {/* Quick links */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', margin: '24px 0', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Jump to section</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {SECTIONS.map(s => (
              <span
                key={s.id}
                onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' })}
                style={{ fontSize: 13, padding: '5px 12px', borderRadius: 20, background: '#e8f0fb', color: BLUE, cursor: 'pointer', fontWeight: 500 }}
              >
                {s.label}
              </span>
            ))}
          </div>
        </div>

        {/* GETTING STARTED */}
        <Section id="getting-started" title="1. Getting Started">
          <p style={{ fontSize: 14, color: '#555', lineHeight: 1.7, marginBottom: 20 }}>
            Welcome to GAA Match Tracker! Follow these steps to get your club set up quickly.
          </p>
          <Step num={1} title="Create your account">
            Go to <strong>gaamatchtracker.ie</strong> and sign up with your email address. The first person to register for a club becomes the club admin automatically.
          </Step>
          <Step num={2} title="Add your club">
            Enter your club name and county during signup. Your public club page will be created automatically at <strong>gaamatchtracker.ie/public/club/[your-club-ID]</strong>.
          </Step>
          <Step num={3} title="Add your squads">
            Go to the <strong>Squad</strong> tab and create a squad for each age group — U10s, U12s, U13s, senior teams, ladies, camogie, hurling. You can have as many squads as you need.
          </Step>
          <Step num={4} title="Invite your coaches">
            Go to <strong>Admin → Invite</strong> to send email invitations to coaches for each squad. They'll be up and running in minutes.
          </Step>
          <Step num={5} title="Add fixtures">
            Go to the <strong>Fixtures</strong> tab and add your upcoming matches. Then share your public page with parents so they can follow live scores.
          </Step>
        </Section>

        {/* MANAGING YOUR SQUAD */}
        <Section id="managing-squad" title="2. Managing Your Squad">
          <Q q="How do I add players to my squad?">
            Go to the <strong>Squad</strong> tab and select your squad. You can add players in three ways:
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li style={{ marginBottom: 4 }}><strong>Individually</strong> — tap + Add Player and enter their details</li>
              <li style={{ marginBottom: 4 }}><strong>Bulk add</strong> — paste a list of names all at once</li>
              <li style={{ marginBottom: 4 }}><strong>Foireann import</strong> — upload your Foireann team sheet PDF and player names, dates of birth and Foireann IDs are imported automatically</li>
            </ul>
          </Q>
          <Q q="What is a guest player?">
            A guest player is someone playing for your team who isn't on your registered squad — for example a player from another age group helping out. You can mark any player as a guest when adding them.
          </Q>
          <Q q="Can I see player stats across the season?">
            Yes — go to the <strong>Squad</strong> tab and tap <strong>Stats</strong> to see scoring records, appearances and more for each player across all tracked matches.
          </Q>
          <Q q="How do I generate a Foireann team sheet?">
            Open a fixture and tap <strong>📄 Team Sheet</strong>. A Foireann-format PDF is generated from your squad data and can be downloaded or emailed directly.
          </Q>
        </Section>

        {/* FIXTURES */}
        <Section id="fixtures" title="3. Fixtures">
          <Q q="How do I add fixtures?">
            Go to the <strong>Fixtures</strong> tab and tap <strong>+ Add</strong>. You can add fixtures in three ways:
            <ul style={{ marginTop: 8, paddingLeft: 20 }}>
              <li style={{ marginBottom: 4 }}><strong>One at a time</strong> — fill in the opponent, date, time and venue</li>
              <li style={{ marginBottom: 4 }}><strong>Bulk add</strong> — enter multiple fixtures at once using the Bulk Add form</li>
              <li style={{ marginBottom: 4 }}><strong>Paste from spreadsheet</strong> — copy and paste a list of fixtures directly from a spreadsheet</li>
            </ul>
          </Q>
          <Q q="Can I see previous results against the same opponent?">
            Yes — on any fixture card, tap <strong>📊 vs [Opponent]</strong> to see all previous results against that team, filtered by squad, sport and team number.
          </Q>
          <Q q="How do I track a live match?">
            Open a fixture and tap <strong>Track Match</strong>. This opens the Match Tracker where you can log scores, cards, substitutions and other events in real time.
          </Q>
        </Section>

        {/* DURING A MATCH */}
        <Section id="during-match" title="4. During a Match">
          <Q q="How do I log a score?">
            In the Match Tracker, tap the <strong>+</strong> button next to Goal or Point for your team or the opposition. You'll be asked whether it was from play, a free or a sideline.
          </Q>
          <Q q="How do I make a substitution?">
            Tap <strong>🔄 Sub</strong> on the Score tab, or use <strong>⇄⇄ Multi-Sub</strong> from the Pitch tab to make multiple substitutions at once.
          </Q>
          <Q q="How do I log a card?">
            Tap <strong>🟨 Card</strong> on the Score tab and select the player and card type (yellow, black or red). For opposition cards tap <strong>🟨 Opp Card</strong> and enter the jersey number.
          </Q>
          <Q q="Can I track opposition players?">
            Yes — open the <strong>Opp</strong> tab in the Match Tracker. You can set up the opposition lineup (1–26), track scores and cards by jersey number, and enter player names after the match for future reference.
          </Q>
          <Q q="What if I make a mistake?">
            Tap <strong>↩ Undo</strong> on the Score tab to remove the most recent event of any type.
          </Q>
          <Q q="Can multiple devices track the same match?">
            Only one device can track (log events) at a time — the designated tracker. Other devices can view the match in read-only mode.
          </Q>
        </Section>

        {/* AFTER A MATCH */}
        <Section id="after-match" title="5. After a Match">
          <Q q="Where do I see results?">
            Go to the <strong>Fixtures</strong> tab and tap <strong>Results</strong> to see all completed matches with scores, scorers and cards.
          </Q>
          <Q q="How do I generate a match report?">
            On any completed result, tap <strong>📰 Match Report</strong>. An AI-generated newspaper-style match report is created from the match events. You can edit it, publish it to your public page, share it on WhatsApp or email it to coaches.
          </Q>
          <Q q="How do I view detailed match stats?">
            Tap <strong>📊 Stats</strong> on any completed result to see a full breakdown including scorers, half-time scores, kick-out stats, cards and substitutions.
          </Q>
          <Q q="How do I generate a team sheet?">
            Tap <strong>📄 Team Sheet</strong> on any fixture to generate a Foireann-format PDF team sheet from your confirmed lineup.
          </Q>
        </Section>

        {/* SHARING WITH PARENTS */}
        <Section id="sharing" title="6. Sharing with Parents">
          <Q q="How do parents follow a live match?">
            Every squad has a public live score page. During a match, share the link with parents and they can follow the live score, scorers and match events in real time — no app or login needed.
            <br /><br />
            Your squad's public page is at: <strong>gaamatchtracker.ie/public/[squad-ID]</strong>
          </Q>
          <Q q="What does the public club page show?">
            Your public club page at <strong>gaamatchtracker.ie/public/club/[your-club-ID]</strong> shows all your club's squads, recent results and upcoming fixtures in one place.
          </Q>
          <Q q="Can parents see match reports?">
            Yes — when a coach publishes a match report, it appears on the public results page for that squad. Parents can also share the match report link directly on WhatsApp.
          </Q>
          <Q q="Do parents need to create an account?">
            No — all public pages are accessible without any login or account.
          </Q>
        </Section>

        {/* ROLES & PERMISSIONS */}
        <Section id="roles" title="7. Roles & Permissions">
          <p style={{ fontSize: 14, color: '#555', lineHeight: 1.7, marginBottom: 20 }}>
            GAA Match Tracker has three coach roles, each with different levels of access:
          </p>

          <div style={{ background: '#f8f8f8', borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <Badge color="#1565c0" bg="#e3f2fd">Club Admin</Badge>
              <span style={{ fontSize: 12, color: '#888' }}>Full access across all squads</span>
            </div>
            <ul style={{ fontSize: 14, color: '#555', lineHeight: 1.8, paddingLeft: 20, margin: 0 }}>
              <li>Full edit access to all squads, fixtures and players</li>
              <li>Approve or decline coach access requests</li>
              <li>Invite coaches by email</li>
              <li>Assign or change coach roles</li>
              <li>Remove coach access</li>
              <li>Generate team sheets and match reports</li>
            </ul>
          </div>

          <div style={{ background: '#f8f8f8', borderRadius: 12, padding: '16px 20px', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <Badge color="#6a1b9a" bg="#f3e5f5">Squad Admin</Badge>
              <span style={{ fontSize: 12, color: '#888' }}>Full access for their squad</span>
            </div>
            <ul style={{ fontSize: 14, color: '#555', lineHeight: 1.8, paddingLeft: 20, margin: 0 }}>
              <li>Full edit access to their squad's fixtures and players</li>
              <li>Approve or decline coach access requests</li>
              <li>Invite coaches by email</li>
              <li>Generate team sheets and match reports</li>
            </ul>
          </div>

          <div style={{ background: '#f8f8f8', borderRadius: 12, padding: '16px 20px', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <Badge color="#555" bg="#f0f0f0">Coach</Badge>
              <span style={{ fontSize: 12, color: '#888' }}>Track matches and view data</span>
            </div>
            <ul style={{ fontSize: 14, color: '#555', lineHeight: 1.8, paddingLeft: 20, margin: 0 }}>
              <li>Track live matches</li>
              <li>View fixtures and results</li>
              <li>Read-only access to player data</li>
            </ul>
          </div>

          <Q q="How does a coach request access?">
            A coach goes to gaamatchtracker.ie and signs up with their email. They select their club and request access. The club admin or squad admin will see a notification on their Fixtures screen and can approve or decline from the Admin tab.
          </Q>
          <Q q="How do I invite a coach directly?">
            Go to <strong>Admin → Invite Coach</strong> and enter their email address. They'll receive an invitation email and their account will be approved automatically when they sign up.
          </Q>
          <Q q="Can I change a coach's role later?">
            Yes — go to <strong>Admin</strong> and find the coach in your team list. You can change their role at any time, up to your own permission level.
          </Q>
        </Section>

        {/* FAQ */}
        <Section id="faq" title="8. FAQ">
          <Q q="Is GAA Match Tracker free to use?">
            Yes — GAA Match Tracker is free to use. Some advanced features may require a subscription in the future, but the core match tracking and public score pages will always be free.
          </Q>
          <Q q="Does it work on mobile?">
            Yes — GAA Match Tracker is designed mobile-first and works on any smartphone browser. No app download is required.
          </Q>
          <Q q="What GAA sports are supported?">
            Football, Hurling, Ladies Football and Camogie are all supported. Dual clubs can have separate squads for each sport.
          </Q>
          <Q q="Can I use it for all age grades?">
            Yes — from U8 to Senior. Age-grade rules (two-point scores, black card temporary substitutions, sin bin, rolling substitutions) are applied automatically based on your squad's age group.
          </Q>
          <Q q="What happens if I lose internet during a match?">
            The match clock continues running locally on your device. Score and event logging may be affected without a connection — another squad admin can also take over tracking responsibilities from their admin tab.
          </Q>
          <Q q="Can multiple coaches track the same match?">
            Only one device can log events at a time. Other devices can view the match in real time in read-only mode.
          </Q>
          <Q q="Is my data secure?">
            Yes — all data is stored securely on Supabase, a GDPR-compliant platform. Player data is only visible to coaches in your club.
          </Q>
          <Q q="Can I export my data?">
            Yes — fixtures and results can be exported from the Fixtures tab. Team sheets can be downloaded as PDFs.
          </Q>
        </Section>

        {/* FEEDBACK */}
        <Section id="feedback" title="9. Feedback & Feature Requests">
          <p style={{ fontSize: 14, color: '#555', lineHeight: 1.7, marginBottom: 20 }}>
            GAA Match Tracker is constantly improving based on feedback from coaches and clubs across Ireland. If you have a suggestion, spotted a bug, or would like to request a new feature, we'd love to hear from you.
          </p>
          <div style={{ background: '#e8f0fb', borderRadius: 12, padding: '20px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: BLUE, marginBottom: 8 }}>
              Have feedback or a feature request?
            </div>
            <p style={{ fontSize: 14, color: '#555', marginBottom: 16 }}>
              Get in touch through our contact page — we read every message and aim to respond within 48 hours.
            </p>
            <span
              onClick={() => window.location.href = '/contact'}
              style={{ display: 'inline-block', padding: '12px 28px', background: BLUE, color: '#fff', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}
            >
              Contact Us →
            </span>
          </div>
        </Section>

      </div>

      {/* Footer */}
      <div style={{ background: '#f0f0f0', borderTop: '1px solid #e0e0e0', padding: '20px 16px', textAlign: 'center' }}>
        <span style={{ fontSize: 13, color: '#aaa' }}>GAA Match Tracker · </span>
        <span onClick={() => window.location.href = '/'} style={{ fontSize: 13, color: BLUE, cursor: 'pointer' }}>gaamatchtracker.ie</span>
        <span style={{ fontSize: 13, color: '#aaa' }}> · </span>
        <span onClick={() => window.location.href = '/contact'} style={{ fontSize: 13, color: BLUE, cursor: 'pointer' }}>Contact</span>
        <span style={{ fontSize: 13, color: '#aaa' }}> · </span>
        <span onClick={() => window.location.href = '/privacy'} style={{ fontSize: 13, color: BLUE, cursor: 'pointer' }}>Privacy</span>
      </div>
    </div>
  )
}